
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthProvider';
import { UserOnboarding } from '@/components/Onboarding/UserOnboarding';
import { StorageConnectionWizard } from '@/components/Storage/StorageConnectionWizard';
import { initializeStorageOnLogin } from '@/utils/storageIsolation';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showStorageWizard, setShowStorageWizard] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (user && !hasInitialized) {
      checkUserStatus();
    } else if (!user) {
      setCheckingStatus(false);
      setHasInitialized(false);
    }
  }, [user, hasInitialized]);

  const checkUserStatus = async () => {
    if (!user) {
      setCheckingStatus(false);
      return;
    }

    try {
      console.log('ProtectedRoute: Checking user status for user ID:', user.id);

      // Check if user has completed onboarding
      const onboardingCompleted = localStorage.getItem('onboarding_completed') === 'true';
      
      if (!onboardingCompleted) {
        // Check user profile completion for onboarding
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile:', profileError);
        }

        const needsOnboarding = !profile?.full_name || 
                               !profile?.job_title || 
                               !profile?.company_name ||
                               !localStorage.getItem('welcome_tour_completed');

        if (needsOnboarding) {
          setShowOnboarding(true);
          setCheckingStatus(false);
          setHasInitialized(true);
          return;
        }
      }

      // Initialize storage silently in the background - don't block the UI
      initializeStorageOnLogin()
        .then((result) => {
          console.log('Storage initialization completed:', result);
          if (!result.success && result.error === 'Storage not configured') {
            // Only show storage wizard if explicitly no configuration exists
            console.log('No storage configuration found, may show wizard later');
          }
        })
        .catch((error) => {
          console.error('Storage initialization error:', error);
          // Don't show errors to user, just log them
        });

      // Allow user to continue using the app
      setCheckingStatus(false);
      setHasInitialized(true);

    } catch (error) {
      console.error('Error checking user status:', error);
      // Don't block the user from using the app
      setCheckingStatus(false);
      setHasInitialized(true);
    }
  };

  const handleStorageConnected = () => {
    console.log('ProtectedRoute: Storage connected successfully');
    setShowStorageWizard(false);
    toast({
      title: "Storage Connected",
      description: "Your storage folder is now accessible.",
    });
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem('onboarding_skipped', 'true');
    setShowOnboarding(false);
  };

  if (loading || checkingStatus) {
    console.log('ProtectedRoute: Still loading...', { loading, checkingStatus, user: !!user });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('ProtectedRoute: No user, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }

  console.log('ProtectedRoute: User authenticated, rendering children');

  return (
    <>
      {children}
      {showStorageWizard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <StorageConnectionWizard 
            onConnectionComplete={handleStorageConnected}
          />
        </div>
      )}
      {showOnboarding && !showStorageWizard && (
        <UserOnboarding 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
    </>
  );
};
